// Moved from src/features/proposalFeature/api/proposalApi.js.
//
// Endpoint keys renamed on the move (getAllProposals->listProposals,
// editProposal->updateProposal, createProposal->submitProposal,
// changeProposalStatus->updateProposalStatus) - NOT a cosmetic rename.
// `_features/proposals/api/proposalApiSlice.js` (untagged, 14 external
// consumers at the time) already injected endpoints with these exact same
// names into the same shared apiSlice, with no `overrideExisting` set on
// either side. RTK Query's default behavior on a duplicate endpoint name is
// "first registration wins, the second is silently skipped" - since the
// untagged slice was always resident, this tagged slice's same-named
// endpoints never actually won registration; every consumer was silently
// riding the untagged version. Renaming resolved the collision instead of
// just relocating it. `createProposal`'s own registration has since been
// confirmed dead (zero consumers anywhere) and dropped from that file.
//
// Every URL below is `/proposal` (singular) - confirmed against
// `_config/apiRoutes`'s `apiRoutes.proposal` map and against
// `proposalApiSlice.js`'s own still-working endpoints, which read from
// that same map. This file previously used `/proposals` (plural) on every
// endpoint - a real, live bug: every submit/update/list call 404'd
// (confirmed via an actual Create Proposal E2E run - `POST /api/proposals`
// returned 404, and the same "Failed to load resource: 404" was showing up
// on the untouched-looking proposals list page too, for the same reason,
// throughout this session, wrongly assumed to be a pre-existing/unrelated
// issue until traced here). `updateProposal` also moved `data._id` out of
// the URL and into the body, matching the old slice's `editProposal`
// exactly (a flat `/proposal` PATCH, not a nested `/proposal/:id`).
//
// `proposalApiSlice.js` itself has since been retired entirely - its two
// genuine duplicates (editProposal/changeProposalStatus, same URL+method as
// updateProposal/updateProposalStatus here, just untagged) are gone; its 3
// non-duplicate endpoints (getAllProposals, getCustomerProposal,
// createSubscriptionFromProposal) are folded in below, unchanged in
// behavior, now tagged so they participate in the same cache-invalidation
// network instead of requiring consumers to manually refetch() after every
// edit/status-change (two of them did, to compensate for the untagged
// slice never invalidating anything).
import { apiRoutes } from "../../../_config/apiRoutes";
import { apiSlice } from "../../../_api/apiSlice";

export const proposalApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listProposals: builder.query({
      query: ({ page, limit, filters }) => ({
        url: `/proposal?page=${page}&limit=${limit}&filters=${filters}`,
        method: "GET",
      }),
      providesTags: ["Proposals"],
    }),
    // Stat cards and tab counts, computed over every proposal on the server.
    getProposalStats: builder.query({
      query: () => ({ url: apiRoutes.proposal.stats.path, method: apiRoutes.proposal.stats.reqType }),
      providesTags: ["Proposals"],
    }),
    // What a new proposal starts from, per the org's Platform Settings (e.g. generateInvoice).
    getProposalDefaults: builder.query({
      query: () => ({ url: apiRoutes.proposal.defaults.path, method: apiRoutes.proposal.defaults.reqType }),
      providesTags: [{ type: "PlatformSettings", id: "SETTINGS" }],
    }),
    // One page of a list tab (mine | stale7d | won).
    getProposalTab: builder.query({
      query: (params) => ({ url: apiRoutes.proposal.tab.path, method: apiRoutes.proposal.tab.reqType, params }),
      providesTags: ["Proposals"],
    }),
    getAllProposals: builder.query({
      query: (params) => ({
        url: apiRoutes.proposal.getAllProposals.path,
        method: apiRoutes.proposal.getAllProposals.reqType,
        params,
      }),
      providesTags: ["Proposals"],
    }),
    updateProposal: builder.mutation({
      query: (data) => ({
        url: "/proposal",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Proposals"],
    }),
    submitProposal: builder.mutation({
      query: (data) => ({
        url: "/proposal",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Proposals"],
    }),
    updateProposalStatus: builder.mutation({
      query: (data) => ({
        url: "/proposal/changeStatus",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Proposals"],
    }),
    getCustomerProposal: builder.query({
      query: (params) => ({
        url: apiRoutes.proposal.getCustomerProposal.path,
        method: apiRoutes.proposal.getCustomerProposal.reqType,
        params,
      }),
      providesTags: (result) =>
        result?.length
          ? result.map((proposal) => ({ type: "Proposals", id: proposal._id }))
          : ["Proposals"],
    }),
    createSubscriptionFromProposal: builder.mutation({
      query: (data) => ({
        url: apiRoutes.proposal.createSubscription.path,
        method: apiRoutes.proposal.createSubscription.reqType,
        body: data,
      }),
      invalidatesTags: ["Proposals"],
    }),
    // Fired once by Launch Flow when the real customer (not an internal
    // preview click) first views their proposal - see useLaunchFlow.js's
    // isInternalViewer check for how that's told apart. `/mark/opened` is a
    // deliberately distinct two-segment path from `/:id` (see
    // routes/proposal.js's comment) - not something to "simplify" back
    // toward `/proposal/${id}/opened`.
    markProposalOpened: builder.mutation({
      query: ({ id }) => ({
        url: "/proposal/mark/opened",
        method: "POST",
        params: { id },
      }),
      invalidatesTags: ["Proposals"],
    }),
    // Renders the org's actual default Proposal template (same backend
    // pipeline `submitProposal`/sendProposal uses - see
    // previewProposalDraft in proposal.controller.js) against the wizard's
    // current, unsaved draft state. Never invalidates/is invalidated by
    // anything - it has no side effects to cache against.
    previewProposal: builder.mutation({
      query: (data) => ({
        url: "/proposal/preview",
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const {
  useListProposalsQuery,
  useGetProposalStatsQuery,
  useGetProposalDefaultsQuery,
  useGetProposalTabQuery,
  useGetAllProposalsQuery,
  useUpdateProposalMutation,
  useSubmitProposalMutation,
  useUpdateProposalStatusMutation,
  useGetCustomerProposalQuery,
  useCreateSubscriptionFromProposalMutation,
  useMarkProposalOpenedMutation,
  usePreviewProposalMutation,
} = proposalApi;
